import React, { useCallback, useEffect, useState } from "react";
import { Ban, Loader2, Users, X } from "lucide-react";
import axios from "axios";

const liveViewerApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL || ""}/api`,
  withCredentials: true,
});

liveViewerApi.interceptors.request.use((config) => {
  let token = null;
  try { token = localStorage.getItem("session_token"); } catch (_) {}
  if (token && !config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const initials = (name) => String(name || "V")
  .replace(/^@/, "")
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase())
  .join("") || "V";

export default function LiveViewerRoster({
  eventId,
  viewerCount = 0,
  isCreator = false,
  buttonClassName = "",
  testId = "live-viewer-count",
}) {
  const [open, setOpen] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blockingId, setBlockingId] = useState("");
  const [error, setError] = useState("");

  const loadViewers = useCallback(async ({ quiet = false } = {}) => {
    if (!eventId) return;
    if (!quiet) setLoading(true);
    setError("");
    try {
      const response = await liveViewerApi.get(`/events/${eventId}/live-viewers`);
      setViewers(response.data?.viewers || []);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Unable to load the live audience.");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (!open) return undefined;
    loadViewers();
    const interval = window.setInterval(() => loadViewers({ quiet: true }), 10_000);
    return () => window.clearInterval(interval);
  }, [loadViewers, open]);

  const blockViewer = async (viewer) => {
    if (!isCreator || !viewer.user_id) return;
    if (!window.confirm(`Block ${viewer.display_name} from this and all future events?`)) return;
    setBlockingId(viewer.user_id);
    try {
      await liveViewerApi.post(`/events/${eventId}/bans/${viewer.user_id}`);
      setViewers((current) => current.filter((row) => row.user_id !== viewer.user_id));
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Unable to block this viewer.");
    } finally {
      setBlockingId("");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`pointer-events-auto flex items-center justify-center gap-1.5 ${buttonClassName}`}
        aria-label={`View ${Number(viewerCount) || 0} people in this live event`}
        title="See who's in this live event"
        data-testid={testId}
      >
        <Users className="h-3.5 w-3.5" />
        <span>{Number(viewerCount) || 0}</span>
      </button>

      {open && (
        <div className="pointer-events-auto fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-label="People in this live event">
          <div className="w-full max-w-sm overflow-hidden rounded-2xl border border-white/15 bg-[#101116] text-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div>
                <h2 className="font-extrabold">People watching</h2>
                <p className="mt-0.5 text-xs text-gray-400">{Number(viewerCount) || 0} live connection(s)</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full p-2 text-gray-300 hover:bg-white/10" aria-label="Close viewer list">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-3" data-testid="live-viewer-roster">
              {loading && <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading viewers…</div>}
              {!loading && error && <p className="rounded-xl bg-red-500/10 p-3 text-sm text-red-300">{error}</p>}
              {!loading && !error && viewers.length === 0 && <p className="py-10 text-center text-sm text-gray-500">No identifiable viewers are connected right now.</p>}
              {!loading && !error && viewers.map((viewer) => (
                <div key={viewer.user_id || viewer.display_name} className="flex items-center gap-3 rounded-xl px-2 py-2.5 hover:bg-white/[.04]">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-blue-600/25 text-xs font-black text-blue-100">
                    {viewer.picture ? <img src={viewer.picture} alt="" className="h-full w-full object-cover" /> : initials(viewer.display_name)}
                  </div>
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold">{viewer.display_name}</span>
                  {isCreator && viewer.user_id && (
                    <button
                      type="button"
                      onClick={() => blockViewer(viewer)}
                      disabled={blockingId === viewer.user_id}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-500/25 bg-red-500/10 px-2.5 py-1.5 text-[11px] font-bold text-red-300 hover:bg-red-500/20 disabled:opacity-50"
                      data-testid={`block-live-viewer-${viewer.user_id}`}
                    >
                      {blockingId === viewer.user_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />} Block
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
